import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import AppTable, { AppTableCell } from "@/features/line-sheet-sets/app-table";
import {
  Brand,
} from "@/features/types";
import {
  ConditionStatistics,
} from "@/features/line-sheet-sets/line-sheet-set.types";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Box, Flex, Text } from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import SortableTableHeader, {
  cycleSortDirection,
  SortDirection,
} from "@/features/line-sheet-sets/detail/sortable-table-header";

interface ConditionStatisticsPanelProps {
  conditionStatistics?: ConditionStatistics | null;
  brands?: Brand[] | null;
}

export default function ConditionStatisticsPanel({
  conditionStatistics,
  brands,
}: ConditionStatisticsPanelProps) {
  const { tTitle } = useI18nHelper();
  // 기본은 접힌 상태 — 행이 많을 때 화면 공간 절약. (ConfirmedConditionPanel과 동일 패턴)
  const [isCollapsed, setIsCollapsed] = useState(true);
  // value(값) 컬럼 정렬 상태. none이면 원본 순서 유지.
  const [sortDir, setSortDir] = useState<SortDirection>("none");

  const brandNameById = useMemo(() => {
    const map = new Map<number, string>();
    (brands ?? []).forEach((b) => map.set(b.id, b.name));
    return map;
  }, [brands]);

  // 정렬 기준이 될 sortKey(셀 안에 여러 값이 묶이면 최대값)를 행마다 함께 보관.
  const baseRows = useMemo(():
    | { sortKey: number; cells: AppTableCell<any>[] }[]
    | undefined => {
    const brandStats = conditionStatistics?.brands ?? [];
    if (brandStats.length === 0) return undefined;

    return brandStats.flatMap((bs) => {
      const name = brandNameById.get(bs.brandId) ?? `#${bs.brandId}`;
      if (bs.conditions.length === 0) {
        return [
          {
            // 값이 없는 행은 정렬 시 항상 끝으로.
            sortKey: Number.NEGATIVE_INFINITY,
            cells: [
              { value: name },
              { value: "-" },
              { value: <Text textAlign={"right"}>-</Text> },
            ],
          },
        ];
      }
      const groups = new Map<"MARKUP" | "DISCOUNT", number[]>();
      bs.conditions.forEach((c) => {
        const arr = groups.get(c.by) ?? [];
        arr.push(c.value);
        groups.set(c.by, arr);
      });

      const order: ("MARKUP" | "DISCOUNT")[] = ["MARKUP", "DISCOUNT"];
      return order
        .filter((by) => groups.has(by))
        .map((by) => {
          const values = groups.get(by)!;
          const valueText = values.map((v) => `${v}%`).join(", ");
          return {
            sortKey: Math.max(...values),
            cells: [
              { value: name },
              { value: by },
              { value: <Text textAlign={"right"}>{valueText}</Text> },
            ],
          };
        });
    });
  }, [conditionStatistics, brandNameById]);

  const rows = useMemo((): AppTableCell<any>[][] | undefined => {
    if (!baseRows) return undefined;
    if (sortDir === "none") return baseRows.map((r) => r.cells);

    const sorted = [...baseRows].sort((a, b) => a.sortKey - b.sortKey);
    if (sortDir === "desc") sorted.reverse();
    return sorted.map((r) => r.cells);
  }, [baseRows, sortDir]);

  if (!rows) return null;

  // 접었을 때 상위 N개는 항상 유지. 행이 N개 이하면 접을 게 없어 토글도 숨김.
  // (ConfirmedConditionPanel과 동일)
  const VISIBLE_WHEN_COLLAPSED = 3;
  const isCollapsible = rows.length > VISIBLE_WHEN_COLLAPSED;
  const displayRows =
    isCollapsible && isCollapsed
      ? rows.slice(0, VISIBLE_WHEN_COLLAPSED)
      : rows;

  return (
    <AppPanel title={tTitle("condition")} isCompact={true}>
      <AppTable
        headers={[
          {
            value: tTitle("common\:brand"),
            width: "minmax(120px, 1fr)",
          },
          {
            value: tTitle("condition"),
            width: "minmax(100px, 172px)",
          },
          {
            value: (
              <SortableTableHeader
                label={tTitle("value")}
                direction={sortDir}
                onClick={() => setSortDir((d) => cycleSortDirection(d))}
              />
            ),
            width: "minmax(80px, 120px)",
            textAlign: "right",
          },
        ]}
        rows={displayRows}
        rowHeight={"32px"}
        gap={"8px"}
        padding={isCollapsible ? "0 32px" : "0 32px 16px 32px"}
      />
      {/* 테이블 아래 토글 — 행이 3개 초과일 때만 노출 */}
      {isCollapsible && (
        <Flex
          as={"button"}
          type={"button"}
          width={"100%"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"4px"}
          padding={"8px"}
          borderTop={!isCollapsed ? "0.5px solid #E5E5E5" : "none"}
          color={"#6F6F6C"}
          fontSize={"12px"}
          cursor={"pointer"}
          userSelect={"none"}
          _hover={{ color: "#1272EF", background: "#F7F9FA" }}
          onClick={() => setIsCollapsed((v) => !v)}
          aria-expanded={!isCollapsed}
        >
          <Text>
            {isCollapsed
              ? `펼치기 (+${rows.length - VISIBLE_WHEN_COLLAPSED})`
              : "접기"}
          </Text>
          <Box
            display={"flex"}
            transform={isCollapsed ? "rotate(0deg)" : "rotate(180deg)"}
            transition={"transform 0.15s"}
          >
            <SVG
              src={"/icons-v1_1/chevron-down.svg"}
              width={"12"}
              height={"12"}
            />
          </Box>
        </Flex>
      )}
    </AppPanel>
  );
}
